const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Drawers (native <dialog>) ---------- */
const Drawer = {
  open(dialog) {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    document.documentElement.classList.add('scroll-lock');
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.setAttribute('data-open', '')));
    dialog.querySelector('video')?.play().catch(() => {});
  },
  close(dialog) {
    if (!dialog?.open) return;
    dialog.removeAttribute('data-open');
    dialog.querySelector('video')?.pause();
    setTimeout(() => {
      dialog.close();
      document.documentElement.classList.remove('scroll-lock');
    }, reduceMotion ? 0 : 260);
  },
};

// Delegated so dialogs re-rendered by the theme editor keep working ('cancel' doesn't bubble, so capture it).
const DIALOGS = 'dialog.drawer, dialog.lightbox';
document.addEventListener('cancel', (event) => {
  if (!event.target.matches?.(DIALOGS)) return;
  event.preventDefault();
  Drawer.close(event.target);
}, true);
document.addEventListener('click', (event) => {
  const dialog = event.target.closest(DIALOGS);
  if (dialog && (event.target === dialog || event.target.closest('[data-drawer-close]'))) Drawer.close(dialog);
});

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-drawer-open]');
  if (!trigger) return;
  event.preventDefault();
  Drawer.open(document.getElementById(trigger.dataset.drawerOpen));
});

/* ---------- Header scroll state ---------- */
// Looked up on use, as the theme editor can replace the header element.
const getHeader = () => document.querySelector('[data-header]');
const updateHeader = () => getHeader()?.toggleAttribute('data-scrolled', window.scrollY > 8);
window.addEventListener('scroll', updateHeader, { passive: true });
updateHeader();

/* ---------- Smart sticky ---------- */
// Sticks just under the header when the element fits the viewport. When it's taller,
// it sticks by its bottom edge instead so the end of it (e.g. gallery thumbnails) is never cut off.
const STICKY_GAP = 32;
const setStickyTop = (el) => {
  const top = (getHeader()?.offsetHeight ?? 0) + STICKY_GAP;
  const bottomAligned = window.innerHeight - el.offsetHeight - STICKY_GAP;
  el.style.setProperty('--sticky-top', `${Math.min(top, bottomAligned)}px`);
};
const stickyObserver = new ResizeObserver((entries) => {
  for (const entry of entries) setStickyTop(entry.target);
});
const initSticky = (root) => {
  for (const el of root.querySelectorAll('[data-sticky]')) stickyObserver.observe(el);
};
window.addEventListener('resize', () => {
  for (const el of document.querySelectorAll('[data-sticky]')) setStickyTop(el);
});

/* ---------- Scroll reveal ---------- */
const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.setAttribute('data-revealed', '');
      revealObserver.unobserve(entry.target);
    }
  },
  { rootMargin: '0px 0px -8% 0px' }
);
const initReveal = (root) => {
  for (const el of root.querySelectorAll('[data-reveal]')) {
    // In the theme editor, show content straight away so edits are always visible.
    if (window.Shopify?.designMode) el.setAttribute('data-revealed', '');
    else revealObserver.observe(el);
  }
};

/* ---------- Setup, and re-setup when the theme editor re-renders a section ---------- */
const initSection = (root) => {
  initSticky(root);
  initReveal(root);
};
initSection(document);
document.addEventListener('shopify:section:load', (event) => initSection(event.target));

/* ---------- Quantity steppers ---------- */
document.addEventListener('click', (event) => {
  const step = event.target.closest('[data-qty-step]');
  if (!step) return;
  const input = step.parentElement.querySelector('input');
  const min = Number(input.min || 0);
  input.value = Math.max(min, Number(input.value) + Number(step.dataset.qtyStep));
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ---------- Cart ---------- */
const Cart = {
  get drawer() {
    return document.getElementById('CartDrawer');
  },

  async post(url, body) {
    const isForm = body instanceof FormData;
    if (isForm) {
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);
    } else {
      body = JSON.stringify({ ...body, sections: ['cart-drawer'], sections_url: window.location.pathname });
    }
    const response = await fetch(url, {
      method: 'POST',
      headers: isForm ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message);
    this.render(data.sections);
    return data;
  },

  render(sections) {
    const html = sections?.['cart-drawer'];
    const current = document.querySelector('[data-cart-content]');
    if (!html || !current) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-content]');
    current.replaceWith(fresh);
    const count = Number(fresh.dataset.count);
    for (const badge of document.querySelectorAll('[data-cart-count]')) {
      badge.textContent = count;
      badge.toggleAttribute('data-empty', count === 0);
    }
  },
};

// Delegated from the document so a cart drawer re-rendered by the theme editor keeps working.
const lineTimers = {};
const changeLine = (line, quantity) => {
  clearTimeout(lineTimers[line]);
  lineTimers[line] = setTimeout(async () => {
    Cart.drawer?.querySelector('[data-cart-body]')?.setAttribute('aria-busy', 'true');
    try {
      await Cart.post('/cart/change.js', { line: Number(line), quantity: Number(quantity) });
    } finally {
      Cart.drawer?.querySelector('[data-cart-body]')?.removeAttribute('aria-busy');
    }
  }, 280);
};
document.addEventListener('change', (event) => {
  const input = event.target.closest('#CartDrawer [data-line-qty]');
  if (input) changeLine(input.dataset.lineQty, input.value);
});
document.addEventListener('click', (event) => {
  const remove = event.target.closest('#CartDrawer [data-line-remove]');
  if (remove) changeLine(remove.dataset.lineRemove, 0);
});

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.error = this.querySelector('[data-form-error]');
    this.form?.addEventListener('submit', (event) => this.#submit(event));
  }

  async #submit(event) {
    if (!Cart.drawer) return;
    event.preventDefault();
    const button = this.form.querySelector('[type="submit"]');
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    this.error.hidden = true;
    try {
      await Cart.post('/cart/add.js', new FormData(this.form));
      Drawer.open(Cart.drawer);
    } catch (error) {
      this.error.textContent = error.message;
      this.error.hidden = false;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}
customElements.define('product-form', ProductForm);

/* ---------- Product gallery ---------- */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-gallery-main] img');
    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) this.show(thumb.dataset.src, thumb);
    });
  }

  show(src, thumb) {
    if (!this.main || !src || this.main.currentSrc === src) return;
    this.main.setAttribute('data-swapping', '');
    const next = new Image();
    next.src = src;
    next.decode().catch(() => {}).then(() => {
      this.main.removeAttribute('srcset');
      this.main.src = src;
      this.main.removeAttribute('data-swapping');
    });
    for (const t of this.querySelectorAll('[data-gallery-thumb]')) {
      t.setAttribute('aria-current', String(t === thumb || t.dataset.src === src));
    }
  }
}
customElements.define('product-gallery', ProductGallery);

/* ---------- Read more (long product descriptions) ---------- */
class ReadMore extends HTMLElement {
  // Only collapse when it hides a meaningful amount of text, not a line or two.
  static MIN_HIDDEN = 80;

  connectedCallback() {
    this.body = this.querySelector('.read-more__body');
    this.button = this.querySelector('.read-more__toggle');
    this.button.addEventListener('click', () => this.#toggle());
    // Fires on first layout, font swaps, viewport changes and when the accordion opens.
    new ResizeObserver(() => this.#measure()).observe(this.body);
  }

  get #collapsedHeight() {
    return parseFloat(getComputedStyle(this).getPropertyValue('--collapsed-height')) || 300;
  }

  #measure() {
    if (this.hasAttribute('data-expanded') || this.body.scrollHeight === 0) return;
    const collapsible = this.body.scrollHeight > this.#collapsedHeight + ReadMore.MIN_HIDDEN;
    this.toggleAttribute('data-collapsible', collapsible);
    this.button.hidden = !collapsible;
  }

  #toggle() {
    const expand = !this.hasAttribute('data-expanded');
    const body = this.body;

    if (expand) {
      body.style.maxHeight = `${body.scrollHeight}px`;
      this.setAttribute('data-expanded', '');
      const done = () => { if (this.hasAttribute('data-expanded')) body.style.maxHeight = 'none'; };
      if (reduceMotion) done(); else body.addEventListener('transitionend', done, { once: true });
    } else {
      // Bring the description back into view first so the page doesn't jump past it.
      if (this.getBoundingClientRect().top < 0) {
        this.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      body.style.maxHeight = `${body.scrollHeight}px`;
      body.offsetHeight; // commit the start height so the collapse animates
      body.style.maxHeight = '';
      this.removeAttribute('data-expanded');
    }

    this.button.setAttribute('aria-expanded', String(expand));
    this.button.textContent = expand ? this.button.dataset.less : this.button.dataset.more;
  }
}
customElements.define('read-more', ReadMore);

/* ---------- Video embed: load the YouTube/Vimeo player only when play is pressed ---------- */
class VideoEmbed extends HTMLElement {
  connectedCallback() {
    this.querySelector('.video-embed__cover')?.addEventListener('click', () => this.#load(), { once: true });
  }

  #load() {
    const iframe = document.createElement('iframe');
    iframe.src = this.dataset.src;
    iframe.title = this.dataset.title;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    this.replaceChildren(iframe);
    iframe.focus();
  }
}
customElements.define('video-embed', VideoEmbed);

/* ---------- Variant picker ---------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.section = this.closest('[data-product]');
    this.addEventListener('change', () => this.#update());
  }

  #update() {
    const selected = [...this.querySelectorAll('fieldset')].map((set) => set.querySelector('input:checked')?.value);
    const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
    const button = this.section.querySelector('[data-add-button]');
    if (!variant) {
      button.disabled = true;
      button.textContent = button.dataset.unavailable;
      return;
    }
    this.section.querySelector('input[name="id"]').value = variant.id;
    this.section.querySelector('[data-price]').innerHTML = variant.price_html;
    button.disabled = !variant.available;
    button.textContent = variant.available ? button.dataset.add : button.dataset.soldOut;
    if (variant.image) this.section.querySelector('product-gallery')?.show(variant.image);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }
}
customElements.define('variant-picker', VariantPicker);

/* ---------- Collection sort ---------- */
document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-sort]');
  if (!select) return;
  const url = new URL(window.location.href);
  url.searchParams.set('sort_by', select.value);
  url.searchParams.delete('page');
  window.location.href = url.toString();
});
